import {
  Badge,
  Box,
  Button,
  Flex,
  IconButton,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Select,
  Spinner,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  Text,
  Tooltip,
  useDisclosure,
} from "@chakra-ui/react";
import { useContext, useEffect, useState } from "react";
import { BsThreeDotsVertical } from "react-icons/bs";
import { toast } from "react-toastify";
import { fetchController } from "../../../utils/FetchController/fetchController";
import { getHostDomain } from "../../../utils/utils";
import Table from "../../Tables/Table";
import { AgencyContext } from "../AgencyProvider/AgencyProvider";
import CreateTeamModal from "./CreateTeamModal";
import InviteModal from "./InviteModal";

const ROLE_COLORS = { owner: "purple", admin: "blue", member: "green" };

const ManageTeams = () => {
  const baseUrl = getHostDomain();
  const { buttonColorScheme, textColor } = useContext(AgencyContext);
  const [teams, setTeams] = useState([]);
  const [activeTeamIndex, setActiveTeamIndex] = useState(0);
  const [teamDetail, setTeamDetail] = useState(null);
  const [loading, setLoading] = useState(false);
  const [currentUserEmail, setCurrentUserEmail] = useState(null);

  const createModal = useDisclosure();
  const inviteModal = useDisclosure();

  const activeTeamId = localStorage.getItem("activeTeamId");

  const fetchTeams = async () => {
    try {
      setLoading(true);
      const res = await fetchController(baseUrl + "/api/v1/team/list", "GET");
      setTeams(res?.data || []);
    } catch {
      toast.error("Failed to load teams");
    } finally {
      setLoading(false);
    }
  };

  const fetchTeamDetail = async (teamId) => {
    try {
      const res = await fetchController(baseUrl + `/api/v1/team/${teamId}`, "GET");
      setTeamDetail(res?.data || null);
    } catch {
      toast.error("Failed to load team details");
    }
  };

  const fetchCurrentUser = async () => {
    try {
      const res = await fetchController(baseUrl + "/api/v1/user/me", "GET");
      setCurrentUserEmail(res?.data?.email);
    } catch {}
  };

  useEffect(() => {
    fetchTeams();
    fetchCurrentUser();
  }, []);

  useEffect(() => {
    if (teams.length > 0) {
      const idx = activeTeamId ? teams.findIndex((t) => t.id === activeTeamId) : 0;
      const resolvedIdx = idx >= 0 ? idx : 0;
      setActiveTeamIndex(resolvedIdx);
      fetchTeamDetail(teams[resolvedIdx].id);
    }
  }, [teams]);

  const handleTabChange = (idx) => {
    setActiveTeamIndex(idx);
    fetchTeamDetail(teams[idx].id);
  };

  const setActiveTeam = (teamId) => {
    localStorage.setItem("activeTeamId", teamId);
    toast.success("Active team switched — refresh to apply to all views");
  };

  const removeMember = async (teamId, userId) => {
    try {
      await fetchController(
        baseUrl + `/api/v1/team/${teamId}/member/${userId}`,
        "DELETE"
      );
      toast.success("Member removed");
      fetchTeamDetail(teamId);
    } catch {
      toast.error("Failed to remove member");
    }
  };

  const changeRole = async (teamId, userId, newRole) => {
    try {
      await fetchController(
        baseUrl + `/api/v1/team/${teamId}/member/${userId}/role`,
        "PUT",
        { role: newRole }
      );
      toast.success("Role updated");
      fetchTeamDetail(teamId);
    } catch {
      toast.error("Failed to update role");
    }
  };

  const currentTeam = teams[activeTeamIndex];
  const isOwner = teamDetail?.members?.find(
    (m) => m.email === currentUserEmail
  )?.role === "owner";

  const membersTableData = (teamDetail?.members || []).map((m) => ({
    Name: `${m.first_name} ${m.last_name}`.trim() || "—",
    Email: m.email,
    Role: (
      <Badge colorScheme={ROLE_COLORS[m.role] || "gray"}>{m.role || "—"}</Badge>
    ),
    Operations: (
      <Flex gap={2} alignItems="center">
        {isOwner && m.role !== "owner" && (
          <Select
            size="xs"
            value={m.role}
            onChange={(e) => changeRole(currentTeam.id, m.user_id, e.target.value)}
            w="100px"
          >
            <option value="admin">Admin</option>
            <option value="member">Member</option>
          </Select>
        )}
        {(isOwner || m.email === currentUserEmail) && m.role !== "owner" && (
          <Tooltip label={m.email === currentUserEmail ? "Leave team" : "Remove member"}>
            <IconButton
              size="xs"
              colorScheme="red"
              variant="ghost"
              icon={<BsThreeDotsVertical />}
              onClick={() => removeMember(currentTeam.id, m.user_id)}
            />
          </Tooltip>
        )}
      </Flex>
    ),
  }));

  const columns = ["Name", "Email", "Role", "Operations"].map((key) => ({
    Header: key,
    accessor: key,
    Filter: ({ column }) => <input {...column.filterProps} />,
  }));

  return (
    <Box border="1px" p="5" borderRadius="md" h="81vh" overflowY="auto">
      <Flex justify="space-between" align="center" mb={4}>
        <Text fontSize="xl" fontWeight="bold" color={textColor}>
          Teams
        </Text>
        <Button colorScheme={buttonColorScheme} size="sm" onClick={createModal.onOpen}>
          + Create Team
        </Button>
      </Flex>

      {loading ? (
        <Flex justify="center" mt={10}>
          <Spinner />
        </Flex>
      ) : teams.length === 0 ? (
        <Box textAlign="center" mt={10} color="gray.500">
          <Text>You don't belong to any team yet.</Text>
          <Button mt={4} colorScheme={buttonColorScheme} onClick={createModal.onOpen}>
            Create your first team
          </Button>
        </Box>
      ) : (
        <Tabs index={activeTeamIndex} onChange={handleTabChange}>
          <TabList>
            {teams.map((t) => (
              <Tab key={t.id}>
                {t.name}
                {t.id === activeTeamId && (
                  <Badge ml={2} colorScheme="green" fontSize="0.6em">active</Badge>
                )}
              </Tab>
            ))}
          </TabList>
          <TabPanels>
            {teams.map((t) => (
              <TabPanel key={t.id} px={0} pt={4}>
                <Flex justify="space-between" align="center" mb={4}>
                  <Flex gap={2} align="center">
                    {t.id !== activeTeamId && (
                      <Button size="xs" variant="outline" onClick={() => setActiveTeam(t.id)}>
                        Set as Active
                      </Button>
                    )}
                  </Flex>
                  <Button
                    size="sm"
                    colorScheme={buttonColorScheme}
                    onClick={inviteModal.onOpen}
                  >
                    Invite User
                  </Button>
                </Flex>
                {teamDetail && teamDetail.id === t.id ? (
                  <Table data={membersTableData} columns={columns} />
                ) : (
                  <Flex justify="center" mt={6}><Spinner /></Flex>
                )}
              </TabPanel>
            ))}
          </TabPanels>
        </Tabs>
      )}

      <CreateTeamModal
        isOpen={createModal.isOpen}
        onClose={createModal.onClose}
        onCreated={fetchTeams}
      />

      {currentTeam && (
        <InviteModal
          isOpen={inviteModal.isOpen}
          onClose={inviteModal.onClose}
          teamId={currentTeam.id}
        />
      )}
    </Box>
  );
};

export default ManageTeams;
